import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import { useAtomValue } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import type { action } from '~/routes/remix-api.home.toggle-home-pin';
import { TOGGLE_HOME_PIN } from '~/routes/remix-api.home.toggle-home-pin';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useRootPermissions, useUserLoggedIn } from '~/modules/user';

type CallbackFn = (error: { __typename: string; Message: string }) => void;

/**
 * Hook to generate the function to toggle the home pin status of a directory
 * item. The function will be `undefined` if the current user does not have
 * permission to edit the home pin status.
 * @param onError Callback function when an error occurs during the API call.
 */
export function useToggleHomePin(onError?: CallbackFn) {
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const fetcher = useFetcher<typeof action>();
    const onErrorRef = useLatest<CallbackFn | undefined>(onError);
    const tracker = useTracker();
    const { user, isLoggedIn } = useUserLoggedIn();
    const { editProfile, editAdvertAdmin } = useRootPermissions();
    const eventData = useRef({
        ItemID: 0,
        ExtraData: {
            pinned: false,
            userId: 0,
            userName: '',
        },
    });

    const toggleHomePin = useCallback(
        (itemID: number, pin: boolean) => {
            if (itemID <= 0) {
                return;
            }
            eventData.current = {
                ItemID: itemID,
                ExtraData: {
                    pinned: pin,
                    userId: user.ID,
                    userName: isLoggedIn
                        ? `${user.FirstName} ${user.LastName}`
                        : '',
                },
            };
            fetcher.submit(
                {
                    itemID: itemID,
                    pin: pin,
                },
                {
                    method: 'post',
                    action: TOGGLE_HOME_PIN,
                }
            );
        },
        [fetcher, user, isLoggedIn]
    );

    useEffect(() => {
        const hasError =
            fetcherIsDone(fetcher) &&
            fetcher.data?.HomePinToggle?.__typename === 'UserError';
        if (hasError) {
            onErrorRef.current?.(fetcher.data.HomePinToggle);
        }
    }, [fetcher, onErrorRef, tracker]);

    useEffect(() => {
        if (
            fetcherIsDone(fetcher) &&
            fetcher.data?.HomePinToggle?.__typename !== 'UserError'
        ) {
            tracker.log('HomePinToggle', {
                url: new URL(window.location.href),
                data: eventData.current,
            });
        }
    }, [fetcher, tracker]);

    return {
        toggleHomePin:
            isDesktop && editProfile && editAdvertAdmin
                ? toggleHomePin
                : undefined,
    };
}
